import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import RoundedInput from '../../components/RoundedInput';
import LiquidButton from '../../components/LiquidButton';
import ProgressIndicators from '../../components/ProgressIndicators';
import LoadingBar from '../../components/LoadingBar';
import { VocabularyItem } from '../../types/api';

interface StudySessionProps {
    vocabularyData: VocabularyItem[];
}

const Container = styled.div`
    margin-bottom: 30px;
`;

const Title = styled.h3`
    margin-bottom: 25px;
    margin-top: 0;
    color: #6b46c1;
    font-size: 1.8rem;
    text-align: center;
    text-shadow: 0 2px 4px rgba(107, 70, 193, 0.2);
`;

const Prompt = styled.div`
    margin-bottom: 25px;
    font-size: 20px;
    color: #7c3aed;
    text-align: center;
    font-weight: 500;
`;

const Result = styled.div<{ isCorrect?: boolean }>`
    margin-top: 20px;
    font-size: 22px;
    font-weight: bold;
    text-align: center;
    padding: 15px;
    border-radius: 12px;
    color: ${(props) => (props.isCorrect ? '#16a34a' : '#dc2626')};
    background: ${(props) =>
        props.isCorrect
            ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
            : 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'};
    border: 2px solid ${(props) => (props.isCorrect ? '#bbf7d0' : '#fecaca')};
`;

const InputContainer = styled.div`
    margin-bottom: 15px;
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 15px;
    justify-content: center;
    margin-bottom: 20px;
`;

const NoVocabularyMessage = styled.div`
    text-align: center;
    color: #7c3aed;
    font-size: 18px;
    margin: 40px 0;
    padding: 20px;
    background: linear-gradient(135deg, #f3e8ff 0%, #faf5ff 100%);
    border: 2px solid #d8b4fe;
    border-radius: 12px;
`;

// Progress Indicator Component
const ProgressIndicatorComponent = ({
    currentIndex,
    total,
}: {
    currentIndex: number;
    total: number;
}) => (
    <ProgressIndicators
        currentIndex={currentIndex}
        total={total}
        type="bar"
        variant="default"
        showText
        textPosition="above"
        color="purple"
        animated
        maxWidth="600px"
    />
);

const StudySession: React.FC<StudySessionProps> = ({ vocabularyData }) => {
    const [currentGuess, setCurrentGuess] = useState<string>('');
    const [match, setMatch] = useState<boolean>(false);
    const [hasGuessed, setHasGuessed] = useState<boolean>(false);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [isAutoProgressing, setIsAutoProgressing] = useState<boolean>(false);

    // Reset to first vocabulary item when vocabulary data changes
    useEffect(() => {
        if (vocabularyData.length > 0) {
            setCurrentIndex(0);
            setCurrentGuess('');
            setHasGuessed(false);
            setMatch(false);
        }
    }, [vocabularyData]);

    // If no vocabulary data, show message
    if (!vocabularyData || vocabularyData.length === 0) {
        return (
            <Container>
                <Title>Study Session</Title>
                <NoVocabularyMessage>
                    No vocabulary data available. Please add some vocabulary words first.
                </NoVocabularyMessage>
            </Container>
        );
    }

    const currentWord = vocabularyData[currentIndex];

    const nextWord = () => {
        const nextIndex = (currentIndex + 1) % vocabularyData.length;
        setCurrentIndex(nextIndex);
        setCurrentGuess('');
        setHasGuessed(false);
        setMatch(false);
    };

    const previousWord = () => {
        const prevIndex = currentIndex === 0 ? vocabularyData.length - 1 : currentIndex - 1;
        setCurrentIndex(prevIndex);
        setCurrentGuess('');
        setHasGuessed(false);
        setMatch(false);
    };

    const checkMatch = () => {
        setHasGuessed(true);
        if (currentWord.japanese === currentGuess) {
            setMatch(true);
            setIsAutoProgressing(true);
            // Automatically progress to next word after a short delay
            setTimeout(() => {
                nextWord();
                setIsAutoProgressing(false);
            }, 1500); // 1.5 second delay to show the correct result
        } else {
            setMatch(false);
        }
    };

    return (
        <Container>
            <Title>Study Session</Title>

            {/* Progress Indicator */}
            <ProgressIndicatorComponent currentIndex={currentIndex} total={vocabularyData.length} />

            <Prompt>
                The word is <b>{currentWord.english}</b>
            </Prompt>
            <InputContainer>
                <RoundedInput
                    id="japanese-guess-input"
                    placeholder="Enter your answer in Japanese"
                    value={currentGuess}
                    onChange={(e) => {
                        setCurrentGuess(e.target.value);
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                            checkMatch();
                        }
                    }}
                />
            </InputContainer>
            <ButtonContainer>
                <LiquidButton onClick={previousWord}>Previous</LiquidButton>
                <LiquidButton onClick={checkMatch}>Check</LiquidButton>
                <LiquidButton onClick={nextWord}>Next</LiquidButton>
            </ButtonContainer>
            {hasGuessed && (
                <Result isCorrect={match}>
                    {match ? 'CORRECT' : `WRONG - Correct answer: ${currentWord.japanese}`}
                </Result>
            )}
            {isAutoProgressing && (
                <LoadingBar
                    text="Loading next word..."
                    duration={1.5}
                    color="green"
                    size="medium"
                />
            )}
        </Container>
    );
};

export default StudySession;
